"use client";
import { useEffect, useRef, useState } from "react";
import styles from "./index.module.css";

import InitialCard from "../common/InitialCard";
import BrandedFooter from "../footer/branded";
import Locations from "./locations";
import Drawermenu from "./drawer-menu";
import {
  searchIcon,
  cartIcon,
  searchWhiteIcon,
  closeIcon,
  brandlogo,
} from "../../assets";

import UserLoginSwitch from "../auth/UserLoginSwitch";
import { useAppDispatch, useAppSelector } from "../slice/hooks";
import { getActiveCart } from "../slice/cart-slice";
import { useHistoryManager } from "../history/useHistoryManager";
import ChatReachOutBox from "./ChatReachOutBox";
import Image from "next/image";
import Skeleton from "react-loading-skeleton";
import { setActiveCategory, setCategories } from "../slice/category-slice";
import {
  setAccessPoint,
  setAccessPointId,
  setAccessPoints,
  setOrganisation,
} from "../slice/accessOrg-slice";
import { store } from "../slice/store";

function StoreBox({
  organisation,
  children,
  modal,
  showCart,
  accessPointId,
  handleLocationChange,
  storePath,
  categories,
  switchCategory,
  activeCategory,
  searchTerm,
  setSearchTerm,
  isPageMode = false,
  headerClassName = "",
  bodyClassName = "",
  showSearchBar = true,
  isHomePage = false,
  searchBarRef = null,
  hasTyped = false,
  setHasTyped,
  accessPoint,
  accessPoints,
  navLocation,
  isSwitching = false,
  showMarketDiv = false,
}) {
  // UI state

  const [isSticky, setIsSticky] = useState(false); // tracks if header should be sticky
  const [showMenu, setShowMenu] = useState(false); // drawer menu toggle
  const [searchFocus, setSearchFocus] = useState(false); // expanded search on mobile

  const [pastHomeSearch, setPastHomeSearch] = useState(false); // track scroll past homepage search

  // Redux state

  const cart = useAppSelector(getActiveCart);

  // refs
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const searchInputRef = useRef(null);

  const { navigateTo } = useHistoryManager(storePath);
  const dispatch = useAppDispatch();

  // --- Cart item count ---
  function calculateTotalItems(cart) {
    cart = cart || {};
    let { paymentItem } = cart;
    paymentItem = paymentItem || [];
    return paymentItem.reduce((total, item) => total + item.unit, 0);
  }
  const cartCount = calculateTotalItems(cart);

  // --- Search URL builder ---
  const urlWithSearchQuery = (term) => {
    if (typeof window !== "undefined") {
      const editedTerm = term.trim().replace(/\s+/g, "-");
      const fullPath = `${storePath}?q=${editedTerm}`;
      navigateTo(fullPath, { override: true }, false);
    }
  };

  const storeBoxLocationChange = (e) => {
    handleLocationChange(e);
  };

  const handleLogoClick = () => {
    if (showSearchBar)
      navigateTo("home", {
        urlData: {
          organisation: organisation,
          accessPointId: accessPointId,
          accessPoint: accessPoint,
          accessPoints: accessPoints,
        },
      });
    else setShowMenu(true);
  };

  const handleLoginClick = () => {
    if (navLocation === "marketplace") {
      const cleanStorePath = String(storePath || "").replace(/\/+$/, "");
      navigateTo(`${cleanStorePath}/auth`);
    } else {
      navigateTo("auth");
    }
  };
  const onLogout = () => {};

  // --- Auto-focus search input when expanded ---
  useEffect(() => {
    if (searchFocus && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchFocus]);

  // --- Track header height for spacer when sticky ---
  useEffect(() => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight);
    }
  }, []);

  // --- Sticky header logic (scroll or body lock) ---
  useEffect(() => {
    const handleScrollOrLock = () => {
      const bodyFixed = document.body.style.position === "fixed";
      if (bodyFixed) {
        setIsSticky(true);
      } else {
        setIsSticky(window.scrollY > 0);
      }
    };

    handleScrollOrLock(); // run on mount

    window.addEventListener("scroll", handleScrollOrLock);

    // Also track body style changes (when search modal locks body)
    const observer = new MutationObserver(() => handleScrollOrLock());
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      window.removeEventListener("scroll", handleScrollOrLock);
      observer.disconnect();
    };
  }, []);

  // --- Handle search input changes ---
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm?.(value);
  };

  const cancelSearch = (e) => {
    e.preventDefault();
    setSearchTerm("");
    setSearchFocus(false);
    setHasTyped?.(false);
  };

  // --- Track if user scrolled past the home page search bar ---
  useEffect(() => {
    if (!isHomePage || !searchBarRef?.current) return;

    const handleScroll = () => {
      if (!searchBarRef?.current) return;
      const rect = searchBarRef.current.getBoundingClientRect();
      const scrolledPast = rect.bottom <= headerHeight; // compare position
      setPastHomeSearch(scrolledPast);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [isHomePage, searchBarRef, headerHeight]);

  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const onScroll = () => {
      setIsAtTop(window.scrollY === 0);
    };

    window.addEventListener("scroll", onScroll);
    onScroll(); // initial check

    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Use debounced search to reduce flicker in UI
  const hasSearch = searchTerm !== "";

  return (
    <>
      <div className={`${styles.page} ${bodyClassName}`}>
        {navLocation === "marketplace" 
        // && searchTerm === "" 
        && showMarketDiv && (
            <div
              style={{
                backgroundColor: "#0e86d4",
                color: "white",
                padding: "1rem",
                cursor: "pointer",
              }}
              onClick={() => {
                navigateTo(
                  "market-place/stores",
                  {
                    urlData: {
                      accessPoint: null,
                      accessPoints: [],
                      accessPointId: null,
                      organisation: null,
                    },
                    onNavigate: (navigate) => {
                      if (navigate) {
                        dispatch(setAccessPointId(null));
                        dispatch(setAccessPoint(null));
                        dispatch(setAccessPoints([]));
                        dispatch(setOrganisation(null));
                        dispatch(setActiveCategory(null));
                        dispatch(setCategories(null));
                      }
                    },
                  },
                  false,
                );
              }}
            >
              <h2>Go to our MarketPlace.</h2>
            </div>
          )}
        {/* Header with sticky behavior */}
        <header
          ref={headerRef}
          className={`${headerClassName} ${
            showSearchBar || isSticky ? styles.stickyHeader : ""
          }`}
          style={
            showSearchBar || isSticky
              ? { top: isAtTop ? "" : "0" }
              : { top: "" }
          }
        >
          <div
            className={styles.headerContent}
            style={
              hasSearch
                ? { paddingTop: ".5rem", paddingBottom: ".5rem" }
                : undefined
            }
          >
            {/* Left side: logo + org name */}

            {!searchFocus && storePath !== "market-place" && (
              <div
                className={`${styles.topimageinitials} ${
                  hasSearch ? styles.searchingActive : ""
                }`}
                onClick={handleLogoClick}
              >
                {isSwitching ? (
                  // Skeleton loader while switching orgs
                  <>
                    <div
                      style={{
                        width: "5rem",
                        height: "5rem",
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginBottom: "0.5rem",
                      }}
                    >
                      <Skeleton cirle height={"5rem"} width={"5rem"} />
                    </div>
                    <div className={styles.handleCard}>
                      <div className={styles.handleName}>
                        <Skeleton width={150} height={20} />
                      </div>
                      <div className={styles.storeFront}>
                        <Skeleton width={100} height={16} />
                      </div>
                    </div>
                  </>
                ) : (
                  // Normal organisation display
                  <>
                    <InitialCard
                      image={organisation?.logoUrl ?? ""}
                      name={organisation?.orgName ?? ""}
                    />
                    <div className={styles.handleCard}>
                      <div className={styles.handleName}>
                        {organisation?.orgName ?? ""}
                      </div>
                      <div className={styles.storeFront}>
                        {(() => {
                          const safePoints = accessPoints || [];
                          const point = safePoints.find(
                            (p) => p.accessPointId === accessPointId,
                          );

                          if (safePoints.length === 0) {
                            return (
                              organisation?.settings?.storeName || "StoreFront"
                            );
                          }

                          if (safePoints.length === 1) {
                            return (
                              organisation?.settings?.storeName || "StoreFront"
                            );
                          }

                          if (point?.name === "Main Entrance") {
                            return (
                              organisation?.settings?.storeName || "StoreFront"
                            );
                          }

                          return `Location: ${
                            point?.name || "Unknown Location"
                          }`;
                        })()}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {!searchFocus && storePath === "market-place" && (
              <div className={styles.marketPlaceHeader}>
                {isSwitching ? (
                  // Skeleton loader while switching orgs
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {/* Logo Skeleton */}
                    <div
                      style={{
                        width: "5rem",
                        height: "5rem",
                        borderRadius: "50%",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <Skeleton circle height="5rem" width="5rem" />
                    </div>

                    {/* Text Skeletons */}
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      <Skeleton width={150} height={20} />
                      <Skeleton width={100} height={16} />
                    </div>
                  </div>
                ) : (
                  // Normal Marketplace header
                  <>
                    <div
                      className={styles.brandImagecontainer}
                      onClick={handleLogoClick}
                    >
                      <Image
                        src={brandlogo}
                        alt="circle plug brand logo"
                        className={styles.brandlogo}
                        quality={1}
                      />
                    </div>
                    <p>Marketplace</p>
                  </>
                )}
              </div>
            )}

            {/* Search bar (desktop, sticky, or focused) */}
            {(showSearchBar || isSticky || searchFocus || hasTyped) &&
              (!isHomePage || searchFocus || hasTyped || pastHomeSearch) && (
                <div
                  className={styles.searchContainer}
                  style={{
                    width: "auto",
                    ...(searchFocus || searchTerm.trim() !== ""
                      ? { display: "block" }
                      : {}),
                  }}
                >
                  <form
                    className={styles.searchbar}
                    onSubmit={(e) => {
                      e.preventDefault();
                      if (searchTerm.trim()) {
                        urlWithSearchQuery(searchTerm);
                        if (searchFocus) setSearchFocus(false);
                      }
                    }}
                  >
                    <input
                      placeholder="Search a product..."
                      ref={searchInputRef}
                      value={searchTerm}
                      onChange={(e) => handleSearch(e)}
                    />

                    {hasTyped ? (
                      <span
                        type="button"
                        className={styles.cancelSearch}
                        onClick={(e) => {
                          cancelSearch(e);
                        }}
                      >
                        {closeIcon}
                      </span>
                    ) : (
                      <button
                        type="submit"
                        className={`${styles.searchButton} ${styles.ovalButton}`}
                      >
                        {searchWhiteIcon} Search
                      </button>
                    )}
                  </form>
                  {searchTerm.trim() !== "" &&
                    storePath !== "market-place" &&
                    navLocation === "marketplace" && (
                      <div
                        className={styles.searchPrompt}
                        style={{
                          paddingTop: ".5rem",
                          paddingLeft: "1rem",
                          color: "#0e86d4",
                          fontSize: "1.2rem",
                          cursor: "pointer",
                        }}
                        onClick={() =>
                          navigateTo(
                            "market-place",
                            {
                              urlData: {
                                accessPoint: null,
                                accessPoints: [],
                                accessPointId: null,
                                organisation: null,
                              },
                              onNavigate: (navigate) => {
                                if (navigate) {
                                  dispatch(setAccessPointId(null));
                                  dispatch(setAccessPoint(null));
                                  dispatch(setAccessPoints([]));
                                  dispatch(setOrganisation(null));
                                }
                              },
                            },
                            false,
                          )
                        }
                      >
                        <p>See all search results from all our stores?..</p>
                      </div>
                    )}
                </div>
              )}

            {/* Right side: locations, search icon, cart, user login */}
            {!searchFocus && (
              <div
                className={`${styles.locationCartBox} ${
                  hasSearch ? styles.searchingActive : ""
                }`}
              >
                {(modal === true && showSearchBar) || !showSearchBar ? (
                  <Locations
                    locations={accessPoints}
                    accessPointId={accessPointId}
                    handleLocationChange={storeBoxLocationChange}
                    storePath={storePath}
                  />
                ) : null}

                {(showSearchBar || isSticky) && !modal && (
                  <div
                    className={styles.searchIconOnly}
                    onClick={() => {
                      setSearchFocus(true);
                      setHasTyped?.(true);
                    }}
                  >
                    {searchIcon}
                  </div>
                )}

                <div
                  className={styles.topCart}
                  onClick={() => {
                    if (!isPageMode) showCart();
                  }}
                  // style={{
                  //   cursor:
                  //     isPageMode || storePath === "market-place"
                  //       ? "not-allowed"
                  //       : "pointer",
                  // }}
                >
                  <div className={styles.topCartIcon}>
                    <span>{cartIcon}</span>
                    {cartCount > 0 && (
                      <sup className={styles.cartCount}>{cartCount}</sup>
                    )}
                  </div>
                  <div className={styles.topCartText}>Cart</div>
                </div>

                <div className="header-login">
                  <UserLoginSwitch
                    onLogout={onLogout}
                    handleLoginClick={handleLoginClick}
                    dropdownMode={true}
                    storePath={storePath}
                    organisation={organisation}
                    accessPointId={accessPointId}
                  />
                </div>
              </div>
            )}
          </div>
        </header>

        {/* Spacer to avoid content jump when header sticks */}
        {(showSearchBar || isSticky) && (
          <div style={{ height: headerHeight }} />
        )}

        {/* Overlay to close mobile search */}
        {searchFocus && !isHomePage && (
          <div
            className={styles.searchOverlay}
            onClick={() => setSearchFocus(false)}
          />
        )}

        {/* Page content */}
        {children}

        {/* Footer */}
        <div className={`${styles.footer} box-footer`}>
          <BrandedFooter
            organisation={organisation}
            storePath={storePath}
            isSwitching={isSwitching}
          />
        </div>
      </div>
      {storePath !== "market-place" && (
        <ChatReachOutBox
          modal={modal}
          organisation={organisation}
          accessPoint={accessPoint}
        />
      )}

      {/* Drawer menu (mobile only) */}
      {showMenu && !showSearchBar ? (
        <Drawermenu
          setShowMenu={setShowMenu}
          showMenu={showMenu}
          categories={categories}
          organisation={organisation}
          accessPoints={accessPoints}
          accessPointId={accessPointId}
          handleLocationChange={storeBoxLocationChange}
          switchCategory={switchCategory}
          activeCategory={activeCategory}
        />
      ) : null}
    </>
  );
}

export default StoreBox;
